/**
 * La striscia di preavviso, e chi la tiene aggiornata.
 *
 * Due pezzi separati apposta: `ProvvederAvvisoFermo` interroga il server e
 * calcola la fase; `StrisciaFermo` la disegna. Il secondo compare in **due**
 * posti — in cima all'applicazione e dentro `Dialogo` — e senza un contesto in
 * mezzo il valore andrebbe passato a mano attraverso undici punti di chiamata.
 */
import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from 'react';

import { ErroreApi } from '../api/client.ts';
import { aggiornamenti } from '../api/scadenziario.ts';
import type { AvvisoFermo as RispostaAvviso } from '../api/tipi-dati.ts';
import { decidiAvviso, formattaRimanente, frasiAvviso, type Avviso } from './avvisoFermo.ts';

/**
 * Ogni trenta secondi.
 *
 * Il conto alla rovescia **non ha bisogno di essere interrogato per scorrere**:
 * una volta noto l'istante del fermo, i secondi li conta il client. Questa
 * interrogazione serve solo ad accorgersi che un'attesa è cominciata, e che è
 * stata annullata. Su un preavviso di minuti, mezzo minuto di ritardo peggiore
 * non toglie niente a nessuno.
 *
 * ⚠️ Sono due chiamate al minuto per scheda, e **toccano il database**: la
 * rotta in sé legge un file, ma l'hook globale di autenticazione risolve il
 * cookie con una join sessioni→utenti a *ogni* richiesta, questa compresa. Il
 * disegno diceva il contrario; i numeri reggono lo stesso, il ragionamento no.
 */
const INTERVALLO_MS = 30_000;

interface Lettura {
  risposta: RispostaAvviso;
  /**
   * Il valore del **cronometro** all'istante della lettura, non l'ora.
   * `performance.now()` è monotono: non torna indietro se l'orologio di
   * sistema viene corretto mentre il conto alla rovescia è in corso.
   */
  cronometroMs: number;
}

const NASCOSTO: Avviso = { fase: 'nascosto', secondiRimasti: 0 };

const ContestoAvvisoFermo = createContext<Avviso | null>(null);

export function ProvvederAvvisoFermo({ children }: { children: ReactNode }) {
  const [lettura, setLettura] = useState<Lettura | null>(null);
  // Serve solo a far ridisegnare: il valore non lo legge nessuno, e
  // `noUnusedLocals` lo pretende — omesso dalla destrutturazione invece di
  // dargli un nome che poi risulterebbe inutilizzato.
  const [, setBattito] = useState(0);

  useEffect(() => {
    let annullato = false;
    let periodico: number | undefined;

    /**
     * Numero d'ordine delle richieste, per non far vincere una risposta
     * vecchia.
     *
     * Due interrogazioni possono sovrapporsi — quella periodica e quella del
     * ritorno sulla scheda partono a un soffio di distanza — e la rete non
     * promette che tornino nell'ordine in cui sono partite. Il danno non è la
     * fase, che si autocorregge al giro dopo: è il **cronometro**, perché
     * `cronometroMs` viene letto all'arrivo. Applicare la risposta perdente
     * accoppierebbe un `adesso` vecchio con un cronometro nuovo, gonfiando il
     * conto alla rovescia di quanto è durata la richiesta persa — e il conto
     * resterebbe gonfio fino alla lettura successiva.
     */
    let ultimaChiesta = 0;
    let ultimaApplicata = 0;

    /**
     * Alzata da un 401: la sessione è caduta e non tornerà da sola.
     *
     * Senza, la striscia continuerebbe a chiedere ogni trenta secondi per
     * sempre — una scheda dimenticata aperta il venerdì sera arriva al lunedì
     * con qualche migliaio di richieste, ognuna delle quali fa comunque la
     * join sessioni→utenti dell'hook di autenticazione. E non servirebbero a
     * niente: senza sessione la risposta è 401 anche fra tre giorni.
     *
     * ⚠️ Fermarsi qui **non** vuol dire riportare nessuno all'accesso: quella
     * è la decisione presa con `getDiFondo`, e resta. Vuol dire smettere di
     * bussare a una porta che si sa chiusa. Chi rientra rimonta il
     * provveditore (sta sotto il controllo della sessione in `App.tsx`) e
     * l'interrogazione riparte da sola.
     */
    let sessioneCaduta = false;

    async function chiedi(): Promise<void> {
      if (annullato || sessioneCaduta) return;

      const mia = ++ultimaChiesta;

      try {
        const risposta = await aggiornamenti.avviso();
        if (annullato) return;

        // Una risposta più vecchia di una già applicata si butta.
        if (mia <= ultimaApplicata) return;
        ultimaApplicata = mia;

        setLettura({ risposta, cronometroMs: performance.now() });
      } catch (e) {
        if (e instanceof ErroreApi && e.nonAutenticato) {
          sessioneCaduta = true;
          if (periodico !== undefined) window.clearInterval(periodico);
          return;
        }

        // ⚠️ **Muto di proposito, e non è una svista.** Se l'interrogazione
        // fallisce si tiene l'ultima lettura buona: a metà conto alla
        // rovescia, «l'API non risponde» vuol dire quasi sempre «il fermo è
        // cominciato», non «è stato annullato». Cancellare la striscia
        // proprio nell'istante in cui l'applicazione sta sparendo sarebbe il
        // contrario di quello che serve.
      }
    }

    void chiedi();
    periodico = window.setInterval(() => void chiedi(), INTERVALLO_MS);

    // Una scheda in secondo piano non ha nessuno che la guardi; quando torna
    // davanti, però, deve dire la verità subito e non fra trenta secondi.
    const alRitorno = (): void => {
      if (document.visibilityState === 'visible') void chiedi();
    };
    document.addEventListener('visibilitychange', alRitorno);

    return () => {
      annullato = true;
      if (periodico !== undefined) window.clearInterval(periodico);
      document.removeEventListener('visibilitychange', alRitorno);
    };
  }, []);

  // Calcolato a ogni disegno, senza `useMemo`: `decidiAvviso` è una
  // sottrazione e tre confronti, e memorizzarla costerebbe più di quanto
  // risparmi. Soprattutto, con `useMemo` il contatore del battito andrebbe
  // messo fra le dipendenze **senza essere letto** dentro il corpo — una
  // dipendenza che mente, che il prossimo lettore toglierebbe in buona fede
  // fermando il conto alla rovescia. Qui il legame è quello vero: cambia lo
  // stato, si ridisegna, si ricalcola.
  const valore = lettura === null
    ? NASCOSTO
    : decidiAvviso({
        fermoPrevistoAlle: lettura.risposta.fermoPrevistoAlle,
        adesso: lettura.risposta.adesso,
        trascorsiDaLetturaMs: performance.now() - lettura.cronometroMs,
      });

  /**
   * Il secondo che scorre. Gira **solo** quando c'è qualcosa da mostrare.
   *
   * ⚠️ La condizione guarda la **fase**, non `fermoPrevistoAlle`. Sembra la
   * stessa cosa e non lo è: il server continua a riportare quel campo anche
   * quando il fermo è scaduto da un pezzo — la scadenza dei dieci minuti è una
   * decisione del client, non un filtro del server. Con la condizione sul campo
   * grezzo, un fermo annunciato e mai avvenuto (l'aggiornatore ucciso da un
   * SIGKILL, la sentinella ferma e quindi nessuno che ripulisca lo stato)
   * avrebbe fatto ridisegnare ogni scheda aperta una volta al secondo **per
   * sempre, a schermo vuoto**. Cioè precisamente il caso che questa riga
   * dovrebbe impedire.
   *
   * Sulla fase, invece, si spegne da sé: mentre è `inCorso` il battito fa
   * crescere il tempo trascorso, ai dieci minuti la fase diventa `nascosto`,
   * e a quel punto l'effetto si smonta.
   */
  useEffect(() => {
    if (valore.fase === 'nascosto') return;

    const orologio = window.setInterval(() => setBattito((n) => n + 1), 1000);
    return () => window.clearInterval(orologio);
  }, [valore.fase]);

  return <ContestoAvvisoFermo.Provider value={valore}>{children}</ContestoAvvisoFermo.Provider>;
}

/**
 * Fuori dal provveditore non si rompe niente: si restituisce «nascosto».
 *
 * Diverso da `useAutenticazione`, che invece lancia — ed è voluto. Quello
 * segnala un errore di montaggio; questo viene chiamato anche da `Dialogo`, che
 * è un componente generico e non deve pretendere un contesto per funzionare.
 * Un dialogo senza striscia è un dialogo che funziona; un dialogo che esplode
 * non lo è.
 */
export function useAvvisoFermo(): Avviso {
  return useContext(ContestoAvvisoFermo) ?? NASCOSTO;
}

export function StrisciaFermo() {
  const avviso = useAvvisoFermo();
  const nascosto = avviso.fase === 'nascosto';

  const { annuncio, annuncioCompleto, invito } = frasiAvviso(avviso.fase, avviso.secondiRimasti);
  const forte = avviso.fase === 'ultimoMinuto' || avviso.fase === 'inCorso';
  const conNumero = avviso.fase !== 'inCorso';

  return (
    <>
      {/* 🔑 **Questa regione sta SEMPRE nel DOM, anche vuota, ed è il motivo
          per cui non è dentro al riquadro qui sotto.**

          Una regione `aria-live` inserita nel documento **già piena** non viene
          annunciata in modo affidabile: i lettori di schermo annunciano i
          *cambiamenti* di una regione che stanno già osservando. Finché tutta
          la striscia compariva insieme al suo testo, il primo avviso — quello
          che dà cinque minuti per salvare — poteva non essere pronunciato
          affatto, e chi non vede lo schermo si accorgeva del fermo solo al
          passaggio all'ultimo minuto, sessanta secondi prima.

          Restando montata da sempre, qui cambia solo il testo, che è
          esattamente ciò che un lettore di schermo annuncia.

          Nascosta con `solo-lettori-schermo` e non con `display: none`: la
          seconda la farebbe saltare come se non ci fosse. */}
      <span role="status" aria-live="polite" className="solo-lettori-schermo">
        {!nascosto && (
          <>
            <strong>{annuncioCompleto}</strong> {invito}
          </>
        )}
      </span>

      {/* Il riquadro visibile compare e sparisce; la regione qui sopra no.
          `aria-hidden` perché la stessa notizia l'ha già ricevuta, per intero,
          chi non vede lo schermo. Il numero sta incastrato **dentro** la frase,
          dove il disegno lo vuole, e non dopo l'invito com'era prima: così
          anche a occhio si legge «...si fermerà fra 4 min 12 s. Salva quello
          che stai scrivendo.» e non due pezzi spaiati. */}
      {!nascosto && (
        <div className={`striscia-fermo${forte ? ' striscia-fermo-forte' : ''}`}>
          <span aria-hidden="true">
            <strong>
              {annuncio}
              {conNumero && (
                <>
                  {' fra '}
                  <span className="striscia-fermo-conto">
                    {formattaRimanente(avviso.secondiRimasti)}
                  </span>
                  {'.'}
                </>
              )}
            </strong>
            {' '}
            {invito}
          </span>
        </div>
      )}
    </>
  );
}

/**
 * Misura l'altezza vera del blocco ancorato in cima e la pubblica in
 * `--altezza-testa`, così il CSS può ancorarci sotto qualcos'altro.
 * Si attacca al contenitore col `ref` che restituisce.
 *
 * 🔴 **Serve perché quell'altezza non è una costante, e trattarla come tale è
 * già costato un difetto.** `.sotto-menu` aveva `top: 72px` — 48 di
 * intestazione più 24 di margine — un numero giusto finché la testa conteneva
 * solo l'intestazione. Con la striscia di preavviso sopra, la prima voce del
 * sotto-menu di Impostazioni finiva **sotto il banner**: vista a schermo da
 * Enrico il 24/8/2026, non dedotta.
 *
 * E non si risolve con un numero più grande: la striscia va a capo su finestre
 * strette e cambia altezza da sola, quindi qualunque costante sarebbe sbagliata
 * per metà delle larghezze. `ResizeObserver` la misura invece di indovinarla —
 * e copre da sé anche le altezze future (una seconda riga di avviso, un
 * carattere più grande scelto nel browser).
 *
 * Sta qui e non in `App.tsx` perché è la striscia a far cambiare
 * quell'altezza: chi un giorno toglierà la striscia troverà questa funzione
 * accanto a lei.
 */
export function useAltezzaTesta(): RefObject<HTMLDivElement | null> {
  const testa = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const elemento = testa.current;
    if (elemento === null) return;

    const scrivi = (): void => {
      document.documentElement.style.setProperty(
        '--altezza-testa',
        `${Math.round(elemento.getBoundingClientRect().height)}px`,
      );
    };

    scrivi();
    const osservatore = new ResizeObserver(scrivi);
    osservatore.observe(elemento);

    return () => {
      osservatore.disconnect();
      // Smontata la testa, la variabile va tolta: lasciarla ferma sull'ultimo
      // valore farebbe ancorare il sotto-menu a un'altezza che non esiste più.
      document.documentElement.style.removeProperty('--altezza-testa');
    };
  }, []);

  return testa;
}
